import { useQuery } from '@tanstack/react-query';
import { fetchQuickLinks } from '../../api/sportsbook-api';
import { RefetchInterval, StaleTime } from '../../sportsbook-constants';
import { QuickLinkDto, QuickLinkTypeDto } from '../../api/sbapi/sbapi-api-model';
import { SPORTSBOOK_QUERY_KEY } from '../queries-constants';
import useTranslations from '~/components/translation/use-translations';
import useSportsbookRoutes from '~/components/sportsbook/lobby/hooks/use-sportsbook-routes';

type Options<TData> = {
	select?: (data: QuickLinkDto[]) => TData;
	staleTime?: StaleTime;
	refetchInterval?: RefetchInterval;
};

export const createQuickLinksQueryKey = () => [SPORTSBOOK_QUERY_KEY, 'quick-links'];
const useQueryQuickLinks = <TData = QuickLinkDto[]>(options?: Options<TData>) =>
	useQuery({
		queryKey: createQuickLinksQueryKey(),
		queryFn: fetchQuickLinks,
		staleTime: options?.staleTime ?? StaleTime.ZERO,
		refetchInterval: options?.refetchInterval,
		select: options?.select,
		meta: {
			errorMessage: 'Failed to fetch sports'
		}
	});

const useQueryExploreQuickLinks = <TData = QuickLinkDto[]>(options?: Options<TData>) => {
	const routes = useSportsbookRoutes();
	const { origin } = window.location;
	const {
		data: quickLinks,
		isError,
		isPending
	} = useQueryQuickLinks({
		select: data => data,
		staleTime: options?.staleTime,
		refetchInterval: options?.refetchInterval
	});
	const { translations, isLoadingTranslations } = useTranslations(
		quickLinks?.map(quickLink => quickLink.labelTextKey).filter(labelKey => labelKey) ?? []
	);
	const quickLinksWithRelevantLabelAndURL =
		quickLinks?.map(quickLink => {
			let labelName = quickLink.labelTextKey ? translations[quickLink.labelTextKey] : quickLink.labelTextKey;
			let { url } = quickLink;
			switch (quickLink.type) {
				case QuickLinkTypeDto.SPORT:
					if (!labelName) {
						labelName = quickLink.sportName;
					}
					url = routes.getSportPageLink(quickLink.sportId, quickLink.sportName);
					break;
				case QuickLinkTypeDto.LEAGUE:
					if (!labelName) {
						labelName = quickLink.leagueName;
					}
					url = routes.getLeaguePageLink(
						quickLink.sportId,
						quickLink.sportName,
						quickLink.leagueId,
						quickLink.leagueName
					);
					break;
				case QuickLinkTypeDto.CUSTOM:
					if (!labelName) {
						labelName = '';
					}
					if (url.includes(origin)) {
						url = url.substring(origin.length);
					}
					break;
				default:
			}
			return { ...quickLink, labelName, url };
		}) ?? [];
	return {
		data: quickLinksWithRelevantLabelAndURL,
		isError,
		isPending: isPending || isLoadingTranslations
	};
};

export default useQueryExploreQuickLinks;
